import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { clone, isEmpty, isNil } from "ramda";
import { toast } from "react-toastify";
import { getMuiTheme } from "../../utils/utils";
import palette from "../../../config/colorPalette";
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, IconButton, MenuItem, TextField } from "@mui/material";
import { handleInterMeasurementTime, offsetPolygonPerSegment, updateData } from "./utils/utils";

import { ErrorText } from "./styled";
import LoadingButton from "@mui/lab/LoadingButton";
import { getAssetsDataAction } from "../../redux/assetsSlice";
import { getConfigDataAction } from "../../redux/configSlice";
import { getPointsByAssets, pingIpAddress } from "../../api";
import { ThemeProvider } from "styled-components";
import { styled } from "@mui/material/styles";
import ChannelInfo from "./components/channelInfo";
import MeasurementInfo from "./components/measurementInfo";
import AddIcon from "@mui/icons-material/Add";
import AlertConfigEquipment from "./components/alertConfigEquipment";
import { toPolygon } from "../Structures/components/EquipmentInfo/utils";
import * as turf from "@turf/turf";

const Configuration = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useMemo(() => getMuiTheme(), []);

  const assets = useSelector((state) => state.assetsData);
  const currentConfig = useSelector((state) => state.configData.data);
  const currentUser = useSelector((state) => state.userData.data);

  const [assetRole, setAssetRole] = useState("");

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedEquipmentID, setSelectedEquipmentID] = useState(null);
  const [assetSensors, setAssetSensors] = useState(null);
  const [channelsSensorsIDs, setchannelSensorsIDs] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dataReloadTime, setDataReloadTime] = useState(0);
  const [alertTimeRange, setAlertTimeRange] = useState(0);
  const [equipments, setEquipments] = useState([]);

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [loadingPing, setLoadingPing] = useState(false);
  const disabled = useMemo(() => !isEmpty(errors) || loading, [errors, loading]);
  const disabledPing = useMemo(
    () => (!isEmpty(errors) && !isNil(errors[selectedEquipmentID + "_IP"])) || loadingPing,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors, loadingPing],
  );
  const equipmentTypes = useSelector((state) => state.equipmentTypeData.data);
  const [openConfigEquipment, setOpenConfigEquipment] = useState(false);

  const [subToggle, setSubToggle] = useState([]);
  const [channelDataToggle, setChannelDataToggle] = useState();
  const [channelDataToggle2, setChannelDataToggle2] = useState();
  const [zoneToggle, setZoneToggle] = useState([]);
  const [mixAreas, setMixAreas] = useState(false);
  const [timeOutDisable, setTimeOutDisable] = useState(false);

  const [realState, setRealState] = useState(true);
  const [equipmentInterMeasurement, setEquipmentInterMeasurement] = useState();
  const [polylines, setPolylines] = useState([]);
  const BootstrapButton = styled(Button)({
    marginBottom: "5%",
    border: "1px solid",
    lineHeight: 1.5,
    backgroundColor: palette.primary,
    borderColor: palette.secondary,
    "&:hover": {
      backgroundColor: palette.link,
      borderColor: palette.secondary,
      boxShadow: "none",
    },
    "&:active": {
      boxShadow: "none",
      backgroundColor: palette.link,
      borderColor: palette.secondary,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.2rem rgba(134,26,33,.5)",
    },
  });

  useMemo(() => {
    async function getAssetInfo() {
      if (!isNil(assets.data) && !isEmpty(assets.data) && !isNil(currentConfig)) {
        //hay que setear el rol general en vez de usar el de usuario
        const assetconfig = currentUser.assetsConfigurations?.[assets.data[0].demarcation];
        setAssetRole(
          currentUser.demarcations.includes("Global") ? "total_admin" : assetconfig.assets[assets.data[0].name],
        );
        setSelectedAsset(assets.data[0]);
        setAlertTimeRange(assets.data[0].alertTimeRange / 60);
        setEquipments(assets.data[0].equipments ?? []);
        setSelectedEquipment(assets.data[0].equipments[0]);
        setSelectedEquipmentID(assets.data[0].equipments[0].equipmentID);
        setDataReloadTime(currentConfig.loadTime);
        setLoadingPoints(true);
        const response = await getPointsByAssets(assets.data[0].name);
        setPolylines(response.data.data);
        if (!isNil(assets.data[0].config) && !isNil(assets.data[0].config.additionalSensors)) {
          const assetSensIDs = [];
          for (let index = 0; index < assets.data[0].config.additionalSensors.sensors.length; index++) {
            assetSensIDs.push(assets.data[0].config.additionalSensors.sensors[index].sensor_ID);
          }
          setAssetSensors(assetSensIDs);
        }
        setLoadingPoints(false);
      }
    }
    getAssetInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.data]);

  useEffect(() => {
    if (!isNil(selectedEquipment) && realState) {
      const newSensorIDs = [];
      for (let indexChannel = 0; indexChannel < selectedEquipment.channels.length; indexChannel++) {
        newSensorIDs.push([]);
        for (let indexSub = 0; indexSub < selectedEquipment.channels[indexChannel].substructures.length; indexSub++) {
          newSensorIDs[indexChannel].push(selectedEquipment.channels[indexChannel].substructures[indexSub].sensors);
        }
      }
      setchannelSensorsIDs(newSensorIDs);
      if (selectedEquipment.equipmentType === "DAS") {
        const dasPolylines = polylines.filter((p) => p.equipment_type === "DAS");
        if (dasPolylines.length > 1 && selectedEquipment.areas.main.length === 1) {
          setMixAreas(true);
        } else {
          setMixAreas(false);
        }
      } else {
        setMixAreas(false);
      }

      const newErrors = {};
      const suma = !isNil(selectedEquipment.channels)
        ? selectedEquipment.channels.reduce(
            (acumulador, canal) => acumulador + (parseFloat(canal.measurement.measurementTime) || 0),
            0,
          )
        : 0;
      const falseStateChannels = selectedEquipment.channels.reduce((indices, valor, indice) => {
        if (!valor.state) {
          indices.push(indice);
        }
        return indices;
      }, []);
      let auxequipmentInterMeasurement = suma;
      for (let indexState = 0; indexState < falseStateChannels.length; indexState++) {
        if (
          !isNil(selectedEquipment.channels[falseStateChannels[indexState]].measurement) &&
          !isNil(selectedEquipment.channels[falseStateChannels[indexState]].measurement.measurementTime)
        ) {
          auxequipmentInterMeasurement =
            auxequipmentInterMeasurement -
            selectedEquipment.channels[falseStateChannels[indexState]].measurement.measurementTime;
        }
      }
      setTimeOutDisable(isNil(selectedEquipment.measurementInfo.timeout));
      if (
        parseFloat(selectedEquipment.measurementInfo.interval) < auxequipmentInterMeasurement &&
        selectedEquipment.equipmentType !== "DAS"
      ) {
        newErrors[selectedEquipmentID + "_frequency"] = "not.higher.than.amount.measurement.time.channels";
      }
      setErrors(newErrors);
      setEquipmentInterMeasurement(suma);
      const newSubToggle = [];
      const newZoneToggle = [];

      const newChannelDataToggle = [];
      const newChannelDataToggle2 = [];
      for (let indexChannel = 0; indexChannel < selectedEquipment.channels.length; indexChannel++) {
        if (isNil(channelDataToggle) || isNil(channelDataToggle[indexChannel])) {
          newChannelDataToggle.push(false);
          newChannelDataToggle2.push(false);
        } else {
          newChannelDataToggle.push(channelDataToggle[indexChannel]);
          newChannelDataToggle2.push(channelDataToggle2[indexChannel]);
        }
        newSubToggle.push([]);
        newZoneToggle.push([]);
        for (let indexSub = 0; indexSub < selectedEquipment.channels[indexChannel].substructures.length; indexSub++) {
          if (isNil(subToggle[indexChannel]) || isNil(subToggle[indexChannel][indexSub])) {
            newSubToggle[indexChannel].push(false);
          } else {
            newSubToggle[indexChannel].push(subToggle[indexChannel][indexSub]);
          }
          newZoneToggle[indexChannel].push([]);
          for (
            let indexZone = 0;
            indexZone < selectedEquipment.channels[indexChannel].substructures[indexSub].zones.length;
            indexZone++
          ) {
            if (
              isNil(zoneToggle[indexChannel]) ||
              isNil(zoneToggle[indexChannel][indexSub]) ||
              isNil(zoneToggle[indexChannel][indexSub][indexZone])
            ) {
              newZoneToggle[indexChannel][indexSub].push(false);
            } else {
              newZoneToggle[indexChannel][indexSub].push(zoneToggle[indexChannel][indexSub][indexZone]);
            }
          }
        }
      }

      setChannelDataToggle(newChannelDataToggle); //Toggle para editar Información de Channels y Subs(Parte de Arriba)
      setChannelDataToggle2(newChannelDataToggle2); //Toggle para abrir unicamente los Collapses de Channels(Parte de Abajo)
      setSubToggle(newSubToggle); // Toggle para abrir Collapses de Subestructuras y editar cantidad de Zonas(Parte de Abajo)
      setZoneToggle(newZoneToggle); //Toggle para abrir Modal de Zonas y editar rangos y configuración de alertas(Parte de Abajo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEquipment]);

  useEffect(() => {
    if (!isNil(currentUser)) {
      dispatch(getAssetsDataAction(currentUser.id));
      dispatch(getConfigDataAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  if (assets.loading || isNil(currentUser) || loadingPoints) {
    return (
      <div
        style={{ height: "92vh", display: "flex", justifyContent: "center", alignItems: "center" }}
      >{`${intl.formatMessage({ id: "loading" })}...`}</div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ fontSize: "x-large", padding: "1rem", width: "100%" }}>
        {intl.formatMessage({ id: "asset.configuration" })}
      </div>
      <Box
        sx={{
          minHeight: 200,
          height: isNil(assets) || isEmpty(assets) ? 400 : "80vh",
          width: "100%",
          padding: "1rem",
          "& .actions": {
            color: palette.secondary,
          },
          "& .textPrimary": {
            color: palette.primary,
          },
          overflow: "auto",
        }}
      >
        {!isNil(selectedAsset) && (
          <div
            style={{
              border: `1px solid ${palette.primary}`,
              position: "relative",
              margin: "2rem 0 0 1rem",
              padding: "0 1rem 1rem 1rem",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-2rem",
                backgroundColor: palette.likeWhite,
                padding: "0.2rem 0.4rem 0.2rem 0",
                left: "-1rem",
              }}
            >
              <TextField
                id="asset_to_configure"
                margin="dense"
                style={{ width: "10rem" }}
                variant="outlined"
                defaultValue={selectedAsset?.name}
                onChange={async (e) => {
                  const selectedAssetName = e.target.value;
                  const newSelectedAsset = assets.data?.find((asset) => asset.name === selectedAssetName);
                  setSelectedAsset(newSelectedAsset);
                  const assetconfig = currentUser.assetsConfigurations?.[newSelectedAsset.demarcation];
                  setAssetRole(
                    currentUser.demarcations.includes("Global") ? "total_admin" : assetconfig.assets[selectedAssetName],
                  );
                  const assetSensIDs = [];
                  for (let index = 0; index < newSelectedAsset.config?.additionalSensors?.sensors.length; index++) {
                    assetSensIDs.push(newSelectedAsset.config.additionalSensors.sensors[index].sensor_ID);
                  }
                  setAlertTimeRange(newSelectedAsset.alertTimeRange / 60);
                  setAssetSensors(assetSensIDs);
                  setEquipments(newSelectedAsset.equipments);
                  setRealState(true);
                  setSelectedEquipment(newSelectedAsset.equipments[0]);
                  setSelectedEquipmentID(newSelectedAsset.equipments[0]?.equipmentID);
                  setLoadingPoints(true);
                  const response = await getPointsByAssets(e.target.value);
                  setPolylines(response.data.data);
                  setLoadingPoints(false);
                }}
                select
                label={intl.formatMessage({ id: "assets" })}
                required
              >
                {!isNil(assets.data) &&
                  assets.data.map((asset, index) => (
                    <MenuItem value={asset.name} key={asset.name + index}>
                      {asset.name}
                    </MenuItem>
                  ))}
              </TextField>
            </div>
            <div
              style={{
                border: `1px solid ${palette.primary}`,
                position: "relative",
                margin: "5rem 0 0 1rem",
                padding: "0 1rem 1rem 1rem",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-2rem",
                  backgroundColor: palette.likeWhite,
                  padding: "0.2rem 0.4rem 0.2rem 0",
                  left: "-1rem",
                }}
              >
                {!isNil(equipments) && !isEmpty(equipments) ? (
                  <TextField
                    id="equipment_to_configure"
                    margin="dense"
                    style={{ width: "10rem" }}
                    variant="outlined"
                    defaultValue={selectedEquipmentID}
                    value={selectedEquipmentID}
                    onChange={(e) => {
                      const selectedEquipmentIDOnChange = e.target.value;
                      const selectedEquipmentChange = equipments.find(
                        (equip) => equip.equipmentID === selectedEquipmentIDOnChange,
                      );
                      setRealState(true);
                      setSelectedEquipment(selectedEquipmentChange);
                      setSelectedEquipmentID(selectedEquipmentIDOnChange);
                    }}
                    select
                    label={intl.formatMessage({ id: "equipment" })}
                    required
                  >
                    {equipments.map((equipment, index) => (
                      <MenuItem value={equipment.equipmentID} key={equipment.equipmentID + index}>
                        {equipment.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
              </div>

              {(assetRole === "total_admin" || assetRole === "administrator") && !isNil(setSelectedEquipmentID) && (
                <div style={{ fontSize: "x-large", marginTop: "3rem", width: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.5rem" }}>
                    {assetRole === "total_admin" && (
                      <>
                        {selectedEquipment.equipmentType === "DAS" ? (
                          <>
                            <TextField
                              id={selectedEquipmentID + "_spatialResolution"}
                              style={{ margin: "0 0.2rem" }}
                              value={selectedEquipment.moreInfo.spatialResolution}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);
                                const index = equipments.findIndex(
                                  (equipment) => equipment.equipmentID === selectedEquipmentID,
                                );
                                const newEquipments = [...equipments];
                                const updatedEquipment = { ...newEquipments[index] };
                                updatedEquipment.moreInfo.spatialResolution = parseFloat(e.target.value);
                                newEquipments[index] = updatedEquipment;
                                setRealState(false);
                                setSelectedEquipment(updatedEquipment);
                                setEquipments(newEquipments);
                              }}
                              disabled={assetRole === "viewer"}
                              error={errors[selectedEquipmentID + "_spatialResolution"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "channel.spatial.resolution" })}
                              required
                              type="number"
                            ></TextField>
                            {!isNil(errors[selectedEquipmentID + "_spatialResolution"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[selectedEquipmentID + "_spatialResolution"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={selectedEquipmentID + "_criticalArea"}
                              style={{ margin: "0 0.2rem" }}
                              value={selectedEquipment.areas?.main?.[0].critical.distance}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);
                                const index = equipments.findIndex(
                                  (equipment) => equipment.equipmentID === selectedEquipmentID,
                                );
                                const newEquipments = [...equipments];
                                const updatedEquipment = {
                                  ...newEquipments[index],
                                  areas: {
                                    ...newEquipments[index].areas,
                                    main: newEquipments[index].areas?.main?.map((area) => ({
                                      ...area,
                                      critical: {
                                        ...area.critical,
                                        distance: parseFloat(e.target.value),
                                      },
                                    })),
                                  },
                                };
                                newEquipments[index] = updatedEquipment;
                                setRealState(false);
                                setSelectedEquipment(updatedEquipment);
                                setEquipments(newEquipments);
                              }}
                              disabled={assetRole === "viewer"}
                              error={errors[selectedEquipmentID + "_criticalArea"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "critical.area" })}
                              type="number"
                              required
                            ></TextField>
                            {!isNil(errors[selectedEquipmentID + "_criticalArea"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[selectedEquipmentID + "_criticalArea"] })}
                              </ErrorText>
                            )}
                            <TextField
                              id={selectedEquipmentID + "_protecionArea"}
                              style={{ margin: "0 0.2rem" }}
                              value={selectedEquipment.areas?.main?.[0].protection.distance}
                              onChange={(e) => {
                                const newErrors = { ...errors };
                                if (isEmpty(e.target.value)) {
                                  newErrors[e.target.id] = "not.empty";
                                } else {
                                  delete newErrors[e.target.id];
                                }
                                setErrors(newErrors);
                                const index = equipments.findIndex(
                                  (equipment) => equipment.equipmentID === selectedEquipmentID,
                                );
                                const newEquipments = [...equipments];
                                const updatedEquipment = {
                                  ...newEquipments[index],
                                  areas: {
                                    ...newEquipments[index].areas,
                                    main: newEquipments[index].areas?.main?.map((area) => ({
                                      ...area,
                                      protection: {
                                        ...area.protection,
                                        distance: parseFloat(e.target.value),
                                      },
                                    })),
                                  },
                                };
                                newEquipments[index] = updatedEquipment;
                                setRealState(false);
                                setSelectedEquipment(updatedEquipment);
                                setEquipments(newEquipments);
                              }}
                              error={errors[selectedEquipmentID + "_protecionArea"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "protection.area" })}
                              type="number"
                              required
                              disabled={assetRole === "viewer"}
                            ></TextField>
                            {!isNil(errors[selectedEquipmentID + "_spatialResolution"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[selectedEquipmentID + "_spatialResolution"] })}
                              </ErrorText>
                            )}
                            {!isNil(polylines) && polylines?.filter((p) => p.equipment_type === "DAS").length > 1 && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    id={"checkbox_areas" + selectedEquipmentID}
                                    sx={{
                                      color: palette.primary,
                                      "&.Mui-checked": {
                                        color: palette.primary,
                                      },
                                    }}
                                    onChange={(e) => {
                                      setMixAreas(e.target.checked);
                                    }}
                                    checked={mixAreas}
                                    disabled={assetRole === "viewer"}
                                  />
                                }
                                label={intl.formatMessage({ id: "mix.areas" })}
                              />
                            )}

                            <Button
                              variant="contained"
                              id="area_button"
                              style={{
                                padding: "0.2rem 0.5rem",
                                backgroundColor: palette.primary,
                                color: "white",
                              }}
                              onClick={async () => {
                                const index = equipments.findIndex(
                                  (equipment) => equipment.equipmentID === selectedEquipmentID,
                                );
                                const newEquipments = [...equipments];
                                const criticalArea = selectedEquipment.areas?.main?.[0].critical.distance;
                                const protectionArea = selectedEquipment.areas?.main?.[0].protection.distance;

                                const critPolies = [];
                                const protPolies = [];
                                let auxPoly = null;
                                for (let indexPolyline = 0; indexPolyline < polylines.length; indexPolyline++) {
                                  if (polylines[indexPolyline].equipment_type === "DAS") {
                                    const polys = await offsetPolygonPerSegment(
                                      polylines[indexPolyline],
                                      criticalArea,
                                      protectionArea,
                                      intl,
                                    );
                                    critPolies.push(polys.criticalCoords);
                                    protPolies.push(polys.protectCoords);
                                  }
                                  if (mixAreas) {
                                    if (isNil(auxPoly)) auxPoly = indexPolyline;
                                  }
                                }
                                let finalCritPoliygon = critPolies[0];
                                let finalProtPoliygon = protPolies[0];
                                if (mixAreas) {
                                  for (let indexCrit = 1; indexCrit < critPolies.length; indexCrit++) {
                                    finalCritPoliygon = turf.union(
                                      turf.featureCollection([
                                        toPolygon(finalCritPoliygon),
                                        toPolygon(critPolies[indexCrit]),
                                      ]),
                                    );
                                  }
                                  for (let indexProt = 1; indexProt < protPolies.length; indexProt++) {
                                    finalProtPoliygon = turf.union(
                                      turf.featureCollection([
                                        toPolygon(finalProtPoliygon),
                                        toPolygon(protPolies[indexProt]),
                                      ]),
                                    );
                                  }
                                }
                                const updatedEquipment = {
                                  ...newEquipments[index],
                                  areas: {
                                    ...newEquipments[index].areas,
                                    main: !mixAreas
                                      ? newEquipments[index].areas?.main?.map((area, i) => ({
                                          ...area,
                                          critical: {
                                            ...area.critical,
                                            geometry: {
                                              ...area.critical.geometry,
                                              coordinates: [critPolies[i]],
                                            },
                                          },
                                          protection: {
                                            ...area.protection,
                                            geometry: {
                                              ...area.protection.geometry,
                                              coordinates: [protPolies[i]],
                                            },
                                          },
                                        }))
                                      : [
                                          {
                                            polyline: auxPoly,
                                            critical: {
                                              distance: criticalArea,
                                              geometry: {
                                                type: "Polygon",
                                                coordinates: finalCritPoliygon.geometry.coordinates,
                                              },
                                            },
                                            protection: {
                                              distance: protectionArea,
                                              geometry: {
                                                type: "Polygon",
                                                coordinates: finalProtPoliygon.geometry.coordinates,
                                              },
                                            },
                                          },
                                        ],
                                  },
                                };
                                console.log(updatedEquipment);
                                newEquipments[index] = updatedEquipment;
                                setSelectedEquipment(updatedEquipment);
                                setEquipments(newEquipments);
                                toast.success(intl.formatMessage({ id: "polygon.calculated" }), {
                                  className: "toast-success",
                                });
                              }}
                              disabled={assetRole === "viewer"}
                            >
                              {intl.formatMessage({ id: "calculate.area" })}
                            </Button>
                          </>
                        ) : (
                          <>
                            <TextField
                              id={selectedEquipmentID + "_frequency"}
                              style={{ margin: "0 0.2rem", width: "13%" }}
                              value={selectedEquipment.measurementInfo.interval}
                              defaultValue={selectedEquipment.measurementInfo.interval}
                              inputProps={{ min: 0, max: 86400 }}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) => {
                                setRealState(false);
                                handleInterMeasurementTime({
                                  actualID: selectedEquipmentID + "_frequency",
                                  equipmentInterMeasurement,
                                  equipments,
                                  errors,
                                  setErrors,
                                  selectedEquipmentID,
                                  selectedEquipment,
                                  setSelectedEquipment,
                                  setEquipments,
                                  value: e.target.value,
                                  setEquipmentInterMeasurement,
                                });
                              }}
                              type="number"
                              error={errors[selectedEquipmentID + "_frequency"] ? true : false}
                              variant="outlined"
                              label={intl.formatMessage({ id: "measure.frequency" })}
                              required
                              disabled={assetRole === "viewer"}
                            ></TextField>
                            {!isNil(errors[selectedEquipmentID + "_frequency"]) && (
                              <ErrorText>
                                {intl.formatMessage({ id: errors[selectedEquipmentID + "_frequency"] })}
                              </ErrorText>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {(assetRole === "total_admin" || assetRole === "administrator") && (
                      <>
                        <TextField
                          id={selectedEquipmentID + "_timeout"}
                          style={{ margin: "0 0.2rem", width: "13%" }}
                          value={selectedEquipment.measurementInfo.timeout}
                          inputProps={{ min: 0, max: 86400 }}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            const newErrors = { ...errors };
                            if (isEmpty(e.target.value)) {
                              newErrors[e.target.id] = "not.empty";
                            } else {
                              delete newErrors[e.target.id];
                            }
                            setErrors(newErrors);
                            const index = equipments.findIndex(
                              (equipment) => equipment.equipmentID === selectedEquipmentID,
                            );
                            const newEquipments = [...equipments];
                            const updatedEquipment = {
                              ...newEquipments[index],
                              measurementInfo: {
                                ...newEquipments[index].measurementInfo,
                                timeout: parseInt(e.target.value),
                              },
                            };
                            newEquipments[index] = updatedEquipment;
                            setRealState(false);
                            setSelectedEquipment(updatedEquipment);
                            setEquipments(newEquipments);
                          }}
                          type="number"
                          error={errors[selectedEquipmentID + "_timeout"] ? true : false}
                          variant="outlined"
                          label={intl.formatMessage({ id: "timeout" })}
                          required
                          disabled={timeOutDisable}
                        ></TextField>
                        {!isNil(errors[selectedEquipmentID + "_timeout"]) && (
                          <ErrorText>{intl.formatMessage({ id: errors[selectedEquipmentID + "_timeout"] })}</ErrorText>
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox
                              id={"checkbox_timeout" + selectedEquipmentID}
                              sx={{
                                color: palette.primary,
                                "&.Mui-checked": {
                                  color: palette.primary,
                                },
                              }}
                              onChange={(e) => {
                                setTimeOutDisable(e.target.checked);
                                const index = equipments.findIndex(
                                  (equipment) => equipment.equipmentID === selectedEquipmentID,
                                );
                                const newEquipments = [...equipments];
                                const updatedEquipment = {
                                  ...newEquipments[index],
                                  measurementInfo: {
                                    ...newEquipments[index].measurementInfo,
                                    timeout: e.target.checked ? null : 100,
                                  },
                                };
                                const newErrors = { ...errors };
                                if (e.target.checked) delete newErrors[selectedEquipmentID + "_timeout"];
                                setErrors(newErrors);

                                newEquipments[index] = updatedEquipment;
                                setRealState(false);
                                setSelectedEquipment(updatedEquipment);
                                setEquipments(newEquipments);
                              }}
                              checked={timeOutDisable}
                            />
                          }
                          label={intl.formatMessage({ id: "disable?" })}
                        />
                        <TextField
                          id={selectedEquipmentID + "_IP"}
                          style={{ margin: "0 0.2rem" }}
                          value={selectedEquipment.moreInfo.ipAddress}
                          onChange={(e) => {
                            const newErrors = { ...errors };
                            const regex =
                              /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                            const isValidInput = regex.test(e.target.value);
                            if (isEmpty(e.target.value)) {
                              newErrors[e.target.id] = "not.empty";
                            } else if (!isValidInput) {
                              newErrors[e.target.id] = "must.ip";
                            } else {
                              delete newErrors[e.target.id];
                            }
                            setErrors(newErrors);
                            const index = equipments.findIndex(
                              (equipment) => equipment.equipmentID === selectedEquipmentID,
                            );
                            const newEquipments = [...equipments];
                            const updatedEquipment = {
                              ...newEquipments[index],
                              moreInfo: {
                                ...newEquipments[index].moreInfo,
                                ipAddress: e.target.value,
                              },
                            };
                            newEquipments[index] = updatedEquipment;
                            setRealState(false);
                            setSelectedEquipment(updatedEquipment);
                            setEquipments(newEquipments);
                          }}
                          error={errors[selectedEquipmentID + "_IP"] ? true : false}
                          variant="outlined"
                          label={intl.formatMessage({ id: "ip.address" })}
                          required
                          disabled={assetRole === "viewer"}
                        ></TextField>
                        {!isNil(errors[selectedEquipmentID + "_IP"]) && (
                          <ErrorText>{intl.formatMessage({ id: errors[selectedEquipmentID + "_IP"] })}</ErrorText>
                        )}
                        <LoadingButton
                          variant="contained"
                          size="large"
                          style={{
                            padding: "0.2rem 0.5rem",
                            backgroundColor: disabledPing ? palette.light : palette.primary,
                            color: "white",
                          }}
                          onClick={async () => {
                            const index = equipments.findIndex(
                              (equipment) => equipment.equipmentID === selectedEquipmentID,
                            );
                            const newEquipments = [...equipments];
                            const updatedEquipment = { ...newEquipments[index] };
                            setLoadingPing(true);
                            await pingIpAddress(selectedEquipmentID, updatedEquipment.moreInfo.ipAddress, intl);
                            setLoadingPing(false);
                          }}
                          loading={loadingPing ?? false}
                          disabled={disabledPing}
                        >
                          PING
                        </LoadingButton>
                      </>
                    )}
                  </div>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {selectedEquipment.equipmentType === "DAS" && (
                  <TextField
                    id={`type_das`}
                    variant="outlined"
                    style={{
                      margin: "0 0.2rem",
                      marginTop: assetRole !== "total_admin" && assetRole !== "administrator" && "3rem",
                      marginLeft: "0.7rem",
                      width: "8rem",
                    }}
                    value={selectedEquipment.type}
                    disabled={true}
                    onChange={(e) => {
                      const index = equipments.findIndex((equipment) => equipment.equipmentID === selectedEquipmentID);

                      const newEquipments = [...equipments];
                      const updatedEquipment = { ...newEquipments[index] };
                      updatedEquipment.type = e.target.value;
                      newEquipments[index] = updatedEquipment;

                      setSelectedEquipment(updatedEquipment);
                      setEquipments(newEquipments);
                    }}
                    select
                    label={intl.formatMessage({ id: "type" })}
                    required
                  >
                    <MenuItem key={`type_SINGLE`} value={`SINGLE`}>
                      {`SINGLE`}
                    </MenuItem>
                    <MenuItem key={`type_DUAL`} value={`DUAL`}>
                      {`DUAL`}
                    </MenuItem>
                    <MenuItem key={`type_DOUBLE`} value={`DOUBLE`}>
                      {`DOUBLE`}
                    </MenuItem>
                  </TextField>
                )}
                {!isNil(currentConfig) && (
                  <Autocomplete
                    multiple
                    id="measurand_select"
                    variant="outlined"
                    options={
                      currentConfig.alertConfiguration?.filter((option) => option.category === "equipment") || []
                    }
                    value={!isNil(selectedEquipment.alertsConfigurations) ? selectedEquipment.alertsConfigurations : []}
                    sx={{
                      width: "16rem", // aumenta el ancho para que quepan más chips
                      ".MuiChip-root": {
                        maxWidth: "100%", // permite que los chips no se corten
                      },
                      ".MuiAutocomplete-tag": {
                        maxWidth: "calc(100% - 16px)", // previene overflow
                      },
                      margin: "0 0.2rem",
                      marginLeft: selectedEquipment.equipmentType !== "DAS" && "0.7rem",
                      marginTop: assetRole !== "total_admin" && assetRole !== "administrator" && "3rem",
                    }}
                    disableCloseOnSelect
                    onChange={(event, values) => {
                      const index = equipments.findIndex((equipment) => equipment.equipmentID === selectedEquipmentID);
                      const newEquipments = [...equipments];
                      const updatedEquipment = { ...newEquipments[index] };
                      updatedEquipment.alertsConfigurations = values;
                      setSelectedEquipment(updatedEquipment);
                      setEquipments(newEquipments);
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" label={intl.formatMessage({ id: "alert.config" })} />
                    )}
                    required
                    disabled={assetRole === "viewer"}
                  />
                )}

                <IconButton
                  style={{
                    backgroundColor: palette.dividerLight,
                    marginTop: assetRole !== "total_admin" && assetRole !== "administrator" && "3rem",
                  }}
                  onClick={() => {
                    //Abrir el Modal para crear una configuración de alerta
                    setOpenConfigEquipment(true);
                  }}
                >
                  <AddIcon
                    sx={{
                      color: palette.primary,
                    }}
                  />
                </IconButton>
                {openConfigEquipment && (
                  <AlertConfigEquipment
                    open={openConfigEquipment}
                    setOpen={setOpenConfigEquipment}
                    configData={currentConfig}
                    selectedAsset={selectedAsset}
                    selectedEquipment={selectedEquipment}
                    equipmentTypes={equipmentTypes}
                    disabled={assetRole === "viewer"}
                  />
                )}
              </div>
              {assetRole === "operator" && (
                <div style={{ fontSize: "x-large", marginTop: "2rem", width: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.5rem" }}></div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "0.5rem",
                  width: "100%",
                  marginLeft: "0.3rem",
                }}
              >
                {!isNil(selectedEquipment) &&
                  selectedEquipment.channels.map((channel, indexChannel) => {
                    return (
                      <div key={indexChannel}>
                        {!isNil(channelDataToggle) && (
                          <>
                            <Button
                              variant="contained"
                              style={{
                                marginLeft: "5%",
                                marginRight: "5%",
                                marginBottom: "5%",
                                marginTop: assetRole === "total_admin" || assetRole === "administrator" ? "5%" : "5%",
                                backgroundColor: palette.secondary,
                                width: "10rem",
                                color: "white",
                              }}
                              key={`openmodal${indexChannel}`}
                              onClick={() => {
                                let newChannel = clone(channelDataToggle);
                                if (newChannel[indexChannel]) {
                                  newChannel[indexChannel] = false;
                                } else {
                                  newChannel[indexChannel] = true;
                                  for (
                                    let indexChannelData = 0;
                                    indexChannelData < channelDataToggle.length;
                                    indexChannelData++
                                  ) {
                                    if (indexChannelData !== indexChannel) {
                                      newChannel[indexChannelData] = false;
                                    }
                                  }
                                }
                                setChannelDataToggle(newChannel);
                              }}
                            >
                              {selectedEquipment.channels[indexChannel].name} - {intl.formatMessage({ id: "channel" })}{" "}
                              {indexChannel + 1}
                            </Button>
                            <ThemeProvider theme={theme}>
                              <ChannelInfo
                                assetSensors={assetSensors}
                                channel={channel}
                                channelDataToggle={channelDataToggle}
                                channelsSensorsIDs={channelsSensorsIDs}
                                equipmentInterMeasurement={equipmentInterMeasurement}
                                equipments={equipments}
                                errors={errors}
                                indexChannel={indexChannel}
                                intl={intl}
                                selectedEquipment={selectedEquipment}
                                selectedEquipmentID={selectedEquipmentID}
                                setChannelDataToggle={setChannelDataToggle}
                                setEquipmentInterMeasurement={setEquipmentInterMeasurement}
                                setEquipments={setEquipments}
                                setErrors={setErrors}
                                setRealState={setRealState}
                                setSelectedEquipment={setSelectedEquipment}
                                setchannelSensorsIDs={setchannelSensorsIDs}
                                BootstrapButton={BootstrapButton}
                                setZoneToggle={setZoneToggle}
                                zoneToggle={zoneToggle}
                                polylines={polylines}
                                key={`channelModals${indexChannel}`}
                                disabled={assetRole === "viewer"}
                              />
                            </ThemeProvider>
                          </>
                        )}
                      </div>
                    );
                  })}
              </div>
              {!isNil(selectedEquipment) && !isNil(channelDataToggle2) && !isNil(currentConfig) && (
                <MeasurementInfo
                  channelToggle={channelDataToggle2}
                  setChannelToggle={setChannelDataToggle2}
                  setSubToggle={setSubToggle}
                  setZoneToggle={setZoneToggle}
                  subToggle={subToggle}
                  zoneToggle={zoneToggle}
                  equipments={equipments}
                  errors={errors}
                  selectedEquipment={selectedEquipment}
                  setEquipments={setEquipments}
                  setSelectedEquipment={setSelectedEquipment}
                  polylineDisplay={polylines}
                  setErrors={setErrors}
                  configData={currentConfig}
                  assetsData={assets.data}
                  selectedAsset={selectedAsset}
                  equipmentTypes={equipmentTypes}
                  disabled={assetRole === "viewer"}
                  intl={intl}
                />
              )}
            </div>
            <LoadingButton
              variant="contained"
              size="large"
              style={{
                marginTop: "1rem",
                padding: "0.2rem 0.5rem",
                backgroundColor: disabled ? palette.light : palette.primary,
                color: "white",
              }}
              loading={loading ?? false}
              disabled={disabled || assetRole === "viewer"}
              onClick={() => {
                updateData({
                  selectedAsset,
                  selectedEquipment,
                  channelsSensorsIDs,
                  alertTimeRange,
                  equipments,
                  setLoading,
                  dispatch,
                  assets,
                  role: assetRole,
                  currentUser,
                  intl,
                });
              }}
            >
              {intl.formatMessage({ id: "save" })}
            </LoadingButton>
          </div>
        )}
        {isNil(selectedAsset) && (
          <div
            style={{ height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}
          >{`${intl.formatMessage({ id: "no.data" })}`}</div>
        )}
      </Box>
    </div>
  );
};

export default Configuration;
